import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/phase3`;

/**
 * Phase3InterviewTracker
 * 
 * Reusable component — drop into the Enterprise tab of any dashboard.
 * Shows all scheduled/completed/scored interviews with action buttons.
 * 
 * Props: none (reads token from localStorage)
 */
function Phase3InterviewTracker() {
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [stats, setStats] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [actionMessage, setActionMessage] = useState({ type: '', text: '' });

  const token = localStorage.getItem('token');

  // ── Fetch interviews
  const fetchInterviews = async () => {
    try {
      setIsLoading(true);
      const url = statusFilter
        ? `${API_BASE}/interviews?status=${statusFilter}`
        : `${API_BASE}/interviews`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        setInterviews(result.data || []);
      } else if (response.status === 401) {
        localStorage.clear();
        navigate('/');
      }
    } catch (error) {
      console.error('Error fetching interviews:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Fetch stats
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        setStats(result.data || null);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    if (token) {
      fetchInterviews();
      fetchStats();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // ── Action: Send Invite
  const handleSendInvite = async (interviewId) => {
    setActionLoading(prev => ({ ...prev, [interviewId]: 'invite' }));
    setActionMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_BASE}/interview/${interviewId}/send-invite`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();

      if (response.ok) {
        setActionMessage({ type: 'success', text: `Invite sent to candidate!` });
        fetchInterviews();
      } else {
        setActionMessage({ type: 'error', text: result.detail || 'Failed to send invite' });
      }
    } catch (error) {
      setActionMessage({ type: 'error', text: 'Connection error' });
    } finally {
      setActionLoading(prev => ({ ...prev, [interviewId]: null }));
      setTimeout(() => setActionMessage({ type: '', text: '' }), 4000);
    }
  };

  // ── Action: Score Interview
  const handleCancel = async (interviewId) => {
    if (!window.confirm('Are you sure you want to cancel this interview? This will refund the usage count.')) return;

    setActionLoading(prev => ({ ...prev, [interviewId]: 'cancel' }));

    try {
      const response = await fetch(`${API_BASE}/interview/${interviewId}/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setActionMessage({ type: 'success', text: 'Interview cancelled and usage refunded.' });
        fetchInterviews();
        fetchStats();
      } else {
        const result = await response.json();
        setActionMessage({ type: 'error', text: result.detail || 'Cancel failed' });
      }
    } catch (error) {
      setActionMessage({ type: 'error', text: 'Connection error' });
    } finally {
      setActionLoading(prev => ({ ...prev, [interviewId]: null }));
      setTimeout(() => setActionMessage({ type: '', text: '' }), 4000);
    }
  };

  // ── Status helpers
  const getStatusBadge = (status) => {
    const styles = {
      scheduled: 'bg-blue-100 text-blue-800 border-blue-200',
      ready: 'bg-cyan-100 text-cyan-800 border-cyan-200',
      in_progress: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      completed: 'bg-orange-100 text-orange-800 border-orange-200',
      processed: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200'
    };
    const icons = {
      scheduled: '📅', ready: '✅', in_progress: '🎙️',
      completed: '⏳', processed: '🏆', cancelled: '❌'
    };
    const labels = {
      scheduled: 'Scheduled', ready: 'Ready', in_progress: 'In Progress',
      completed: 'Completed', processed: 'Scored', cancelled: 'Cancelled'
    };

    const s = status?.toLowerCase() || 'scheduled';
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${styles[s] || styles.scheduled}`}>
        <span>{icons[s] || '📅'}</span>
        {labels[s] || status}
      </span>
    );
  };

  const getRecommendationBadge = (rec) => {
    if (!rec) return null;
    const styles = {
      EXCEEDS_BAR: 'bg-green-100 text-green-800 border-green-300',
      MEETS_BAR: 'bg-blue-100 text-blue-800 border-blue-300',
      BORDERLINE: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      BELOW_BAR: 'bg-red-100 text-red-800 border-red-300'
    };
    const icons = { EXCEEDS_BAR: '🌟', MEETS_BAR: '✅', BORDERLINE: '⚠️', BELOW_BAR: '❌' };
    const labels = { EXCEEDS_BAR: 'Exceeds Bar', MEETS_BAR: 'Meets Bar', BORDERLINE: 'Borderline', BELOW_BAR: 'Below Bar' };

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${styles[rec] || ''}`}>
        <span>{icons[rec] || ''}</span>
        {labels[rec] || rec}
      </span>
    );
  };

  // ── Get available actions per status
  const getActions = (interview) => {
    const s = interview.status?.toLowerCase();
    const id = interview.id;
    const loading = actionLoading[id];
    const btnBase = "px-3 py-1.5 rounded-lg text-xs font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed";

    const actions = [];

    if (s === 'scheduled') {
      actions.push(
        <button key="invite" onClick={() => handleSendInvite(id)} disabled={!!loading}
          className={`${btnBase} bg-blue-100 text-blue-700 hover:bg-blue-200 border border-blue-300`}>
          {loading === 'invite' ? '⏳ Sending...' : '📧 Send Invite'}
        </button>
      );
      actions.push(
        <button key="cancel" onClick={() => handleCancel(id)} disabled={!!loading}
          className={`${btnBase} bg-red-50 text-red-600 hover:bg-red-100 border border-red-200`}>
          {loading === 'cancel' ? '⏳...' : '✕ Cancel'}
        </button>
      );
    }

    if (s === 'ready') {
      actions.push(
        <button key="cancel2" onClick={() => handleCancel(id)} disabled={!!loading}
          className={`${btnBase} bg-red-50 text-red-600 hover:bg-red-100 border border-red-200`}>
          ✕ Cancel
        </button>
      );
    }

    // Scoring and report generation are automatic (webhook + fallback pipeline).
    // No manual buttons needed — just navigate to the detail page.

    // View Details — available for all statuses
    actions.push(
      <button key="detail" onClick={() => navigate(`/phase3/interview/${id}`)}
        className={`${btnBase} bg-indigo-100 text-indigo-700 hover:bg-indigo-200 border border-indigo-300`}>
        📋 View Details
      </button>
    );

    return actions;
  };

  return (
    <div className="space-y-6 mt-8">
      {/* ── Stats Row */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <p className="text-xs text-blue-600 font-semibold uppercase">Total</p>
            <p className="text-2xl font-bold text-blue-900">{stats.total || 0}</p>
          </div>
          <div className="bg-cyan-50 rounded-xl p-4 border border-cyan-200">
            <p className="text-xs text-cyan-600 font-semibold uppercase">Scheduled</p>
            <p className="text-2xl font-bold text-cyan-900">{stats.scheduled || 0}</p>
          </div>
          <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
            <p className="text-xs text-yellow-600 font-semibold uppercase">In Progress</p>
            <p className="text-2xl font-bold text-yellow-900">{stats.in_progress || 0}</p>
          </div>
          <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
            <p className="text-xs text-orange-600 font-semibold uppercase">Completed</p>
            <p className="text-2xl font-bold text-orange-900">{stats.completed || 0}</p>
          </div>
          <div className="bg-green-50 rounded-xl p-4 border border-green-200">
            <p className="text-xs text-green-600 font-semibold uppercase">Scored</p>
            <p className="text-2xl font-bold text-green-900">{stats.processed || 0}</p>
          </div>
        </div>
      )}

      {/* ── Action Message */}
      {actionMessage.text && (
        <div className={`rounded-xl p-4 flex items-center gap-3 border-2 ${
          actionMessage.type === 'success'
            ? 'bg-green-50 border-green-400 text-green-700'
            : 'bg-red-50 border-red-400 text-red-700'
        }`}>
          <span className="text-xl">{actionMessage.type === 'success' ? '✅' : '❌'}</span>
          <p className="font-bold text-sm">{actionMessage.text}</p>
        </div>
      )}

      {/* ── Filter + Header */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <h2 className="text-xl font-bold text-gray-900">Interview Tracker</h2>
          <div className="flex items-center gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border-2 border-gray-300 rounded-xl text-sm font-medium focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
            >
              <option value="">All Statuses</option>
              <option value="scheduled">Scheduled</option>
              <option value="ready">Ready</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="processed">Scored</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <button
              onClick={() => { fetchInterviews(); fetchStats(); }}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm font-bold text-gray-700 transition-all border border-gray-300"
            >
              ↻ Refresh
            </button>
          </div>
        </div>

        {/* ── Table */}
        {isLoading ? (
          <div className="p-16 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-blue border-t-transparent mx-auto"></div>
            <p className="text-gray-600 mt-4 font-medium">Loading interviews...</p>
          </div>
        ) : interviews.length === 0 ? (
          <div className="p-16 text-center">
            <div className="text-6xl mb-4">📭</div>
            <p className="text-gray-600 text-lg font-medium">No interviews found</p>
            <p className="text-gray-500 text-sm mt-1">Schedule your first interview above</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wide">Candidate</th>
                  <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wide">Role / Stack</th>
                  <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wide">Level</th>
                  <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wide">Date</th>
                  <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wide">Score</th>
                  <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {interviews.map((interview) => (
                  <tr key={interview.id} className="hover:bg-blue-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-bold text-gray-900">{interview.candidate_name}</p>
                        <p className="text-xs text-gray-500">{interview.candidate_email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{interview.role_type || '—'}</p>
                        <p className="text-xs text-gray-500">{interview.tech_stack || '—'}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-gray-700 capitalize">
                        {interview.interview_level || '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-700 font-medium">
                        {interview.scheduled_datetime
                          ? new Date(interview.scheduled_datetime).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric'
                            })
                          : '—'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {interview.scheduled_datetime
                          ? new Date(interview.scheduled_datetime).toLocaleTimeString('en-US', {
                              hour: '2-digit', minute: '2-digit'
                            })
                          : ''}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(interview.status)}
                    </td>
                    <td className="px-6 py-4">
                      {interview.overall_score ? (
                        <div>
                          <p className="font-bold text-gray-900">{interview.overall_score}/400</p>
                          {interview.recommendation && getRecommendationBadge(interview.recommendation)}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-2">
                        {getActions(interview)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Phase3InterviewTracker;